import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Img, SIZES } from "@/components/shared/Img";

export const Container = ({ children, className, as: Tag = "div" }) => (
  <Tag className={cn("mx-auto w-full max-w-site px-5 sm:px-8 lg:px-10", className)}>{children}</Tag>
);

/** Small uppercase label, optionally numbered. */
export const Eyebrow = ({ num, children, className, light = false }) => (
  <p className={cn("t-mono flex flex-wrap items-center gap-x-2", light ? "text-paper/55" : "text-muted-foreground", className)}>
    {num && <span className="tabular">{num}</span>}
    {num && <span aria-hidden="true">—</span>}
    <span>{children}</span>
  </p>
);

/** Wrap one word of a headline in the accent colour. `*word*` marks it; falls back to `fallback`. */
export const Swash = ({ text = "", fallback, className }) => {
  if (!text) return null;
  const m = text.match(/\*([^*]+)\*/);
  let before = text, word = null, after = "";
  if (m) {
    before = text.slice(0, m.index); word = m[1]; after = text.slice(m.index + m[0].length);
  } else if (fallback) {
    const i = text.toLowerCase().indexOf(fallback.toLowerCase());
    if (i >= 0) { before = text.slice(0, i); word = text.slice(i, i + fallback.length); after = text.slice(i + fallback.length); }
  }
  if (!word) return <>{text.replace(/\*/g, "")}</>;
  return (<>{before}<em className={cn("t-swash", className)}>{word}</em>{after}</>);
};

/** Text link with a nudging arrow. */
export const ArrowLink = ({ to, href, children, className, up = false, light = false, ...props }) => {
  const cls = cn("group inline-flex items-center gap-2 text-[15px] font-medium", up ? "arrow-nudge" : "arrow-nudge-x", light ? "text-paper" : "text-foreground", className);
  const Icon = up ? ArrowUpRight : ArrowRight;
  const inner = (<><span className="u-hover">{children}</span><span className={cn("flex h-7 w-7 items-center justify-center rounded-full", light ? "bg-paper/15" : "bg-secondary")}><Icon className="h-3.5 w-3.5" /></span></>);
  if (href) return <a href={href} className={cls} {...props}>{inner}</a>;
  return <Link to={to} className={cls} {...props}>{inner}</Link>;
};

/** Page intro: label + oversized title, intro to the right on desktop. */
export const PageHeader = ({ eyebrow, num, title, intro, children, image, imageAlt = "", imageCaption, className, compact = false }) => (
  <section className={cn(className)} data-testid="page-header">
    <Container className={compact ? "pb-6 pt-8 sm:pt-12" : "pb-8 pt-10 sm:pt-16 lg:pt-20"}>
      <div className="grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-10">
        <div className="lg:col-span-8">
          {eyebrow && <Eyebrow num={num}>{eyebrow}</Eyebrow>}
          <h1 className="t-h1 mt-4 text-balance" data-testid="page-title">{title}</h1>
        </div>
        {intro && <p className="t-lead max-w-md text-muted-foreground text-pretty lg:col-span-4 lg:pb-3">{intro}</p>}
      </div>
      {children}
      {image && (
        <figure className="mt-10 sm:mt-14">
          <div className="img-hover overflow-hidden rounded-[12px] bg-secondary"><Img src={image} alt={imageAlt} width={1600} sizes={SIZES.full} priority className="aspect-[3/2] w-full object-cover sm:aspect-[21/9]" data-testid="page-header-image" /></div>
          {imageCaption && <figcaption className="mt-3 text-[13px] text-muted-foreground">{imageCaption}</figcaption>}
        </figure>
      )}
    </Container>
  </section>
);

/** Section with label, title + action. `dark` renders as an inset ink block. */
export const Section = ({ eyebrow, num, title, subtitle, action, children, className, id, dark = false, tight = false }) => {
  const head = (title || action || eyebrow) && (
    <div className="mb-10 flex flex-col gap-6 lg:mb-14 lg:flex-row lg:items-end lg:justify-between">
      <div className="max-w-3xl">
        {eyebrow && <Eyebrow num={num} light={dark}>{eyebrow}</Eyebrow>}
        {title && <h2 className={cn("t-h2 mt-4 text-balance", dark && "text-paper")}>{title}</h2>}
        {subtitle && <p className={cn("t-lead mt-5 max-w-2xl text-pretty", dark ? "text-paper/70" : "text-muted-foreground")}>{subtitle}</p>}
      </div>
      {action && <div className="shrink-0 lg:pb-2">{action}</div>}
    </div>
  );
  if (dark) {
    return (
      <section id={id} className={cn("grain my-10 scroll-mt-24 bg-ink py-16 text-paper sm:my-14 sm:py-20 lg:my-16 lg:py-24", className)}>
        <Container>
          {head}
          {children}
        </Container>
      </section>
    );
  }
  return (
    <section id={id} className={cn("scroll-mt-24", tight ? "py-12 sm:py-16" : "py-16 sm:py-20 lg:py-24", className)}>
      <Container>
        {head}
        {children}
      </Container>
    </section>
  );
};

/** Content column that keeps long text readable. */
export const Body = ({ children, className }) => (
  <div className={cn("max-w-5xl", className)}>{children}</div>
);

/** Kept for compatibility – render nothing. */
export const Marquee = () => null;
export const Stamp = () => null;

/** Anchor chips used under a PageHeader for in-page navigation. */
export const AnchorPills = ({ items = [], testPrefix = "anchor", children }) => (
  <nav className="mt-8 flex flex-wrap gap-2" aria-label="Snarvegar på sida">
    {items.map(([href, label]) => (
      <a key={href} href={href} className="inline-flex h-10 items-center rounded-[8px] border border-foreground/20 px-4 text-[14px] font-medium transition-colors hover:border-foreground hover:bg-foreground hover:text-background" data-testid={`${testPrefix}-${href.slice(1)}`}>{label}</a>
    ))}
    {children}
  </nav>
);

/** Big numeric stat. */
export const Stat = ({ value, label, light = false, className }) => (
  <div className={cn(className)}>
    <div className={cn("font-display text-[clamp(44px,5vw,84px)] font-semibold leading-[0.9] tracking-[-0.045em] tabular", light ? "text-paper" : "text-foreground")}>{value}</div>
    <div className={cn("mt-3 text-[15px]", light ? "text-paper/65" : "text-muted-foreground")}>{label}</div>
  </div>
);

/** Dark call-to-action band (full-bleed). */
export const CtaBand = ({ eyebrow, title, text, children, className, image }) => (
  <section className={cn("grain my-10 bg-ink py-16 text-paper sm:my-14 sm:py-20 lg:my-16 lg:py-24", className)}>
    <Container>
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-8">
        <div className={image ? "lg:col-span-8" : "lg:col-span-10"}>
          {eyebrow && <Eyebrow light>{eyebrow}</Eyebrow>}
          <h2 className="t-h2 mt-4 max-w-4xl text-balance text-paper">{title}</h2>
          {text && <p className="t-lead mt-6 max-w-2xl text-paper/75 text-pretty">{text}</p>}
          {children && <div className="mt-10 flex flex-wrap gap-3">{children}</div>}
        </div>
        {image && <div className="lg:col-span-4"><figure className="img-hover overflow-hidden rounded-[12px]"><Img src={image} alt="" width={800} sizes={SIZES.third} className="aspect-[4/3] w-full object-cover lg:aspect-[3/4]" /></figure></div>}
      </div>
    </Container>
  </section>
);
